import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001/api';

// Resolve a stored asset URL to one the browser can fetch. Backend returns
// relative `/uploads/...` paths in local-storage mode (served from the API
// host, NOT the React dev server) and absolute https URLs once swapped to R2.
export const resolveAssetUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads/')) {
    const host = API_BASE.replace(/\/api\/?$/, '');
    return `${host}${url}`;
  }
  return url;
};

// Build the {src, srcSet} pair for an uploaded image. The backend writes
// _256 / _512 / _1024 variants alongside every uploaded image (see
// backend/lib/storage.js — generateImageVariants). The variant filename
// convention is `<original>_<width>.<ext>` so we can construct the URLs
// here without storing them in the DB. Variants are written for every
// upload regardless of source size — small originals just pass through
// at their actual width via sharp's `withoutEnlargement: true`, so the
// srcSet entry always resolves to a real file.
//
// Legacy images (uploaded before the variant pipeline shipped) get
// caught by scripts/backfillImageVariants.js, which regenerates and
// uploads variants for everything in the DB.
//
// Falls back to original-only for non-image URLs (PDFs handed in by
// mistake, external thumbnails without standard extensions).
//
// Usage:
//   const { src, srcSet } = responsiveSrc(artist.profile_picture_url);
//   <img src={src} srcSet={srcSet} sizes="(max-width:768px) 100vw, 256px"
//        alt="..." loading="lazy" />
const VARIANT_WIDTHS = [256, 512, 1024];
const VARIANT_RE = /\.(jpg|jpeg|png|webp)$/i;
export const responsiveSrc = (url) => {
  const resolved = resolveAssetUrl(url);
  if (!resolved || !VARIANT_RE.test(resolved)) {
    return { src: resolved, srcSet: undefined };
  }
  const dot = resolved.lastIndexOf('.');
  const base = resolved.slice(0, dot);
  const ext = resolved.slice(dot);
  const srcSet = VARIANT_WIDTHS.map(w => `${base}_${w}${ext} ${w}w`).join(', ');
  return { src: resolved, srcSet };
};

const api = axios.create({
  baseURL: API_BASE,
  // 20s timeout so a flaky network or stuck server doesn't leave a
  // submit/save hanging on a spinner forever. Per-call overrides via
  // `{ timeout: ... }` are still respected — uploads pass a longer
  // value at their callsite.
  timeout: 20000,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If the backend returns 403 "Account suspended" on an authenticated request,
// the session is dead — clear local creds and bounce to login with the reason.
// If the backend returns 403 with code 'email_unverified', dispatch a window
// event so a global listener (UnverifiedActionModal) can prompt the user to
// verify rather than every call site rolling its own toast.
// Pages where an expired/invalid token shouldn't trigger a redirect — they
// either don't require auth or already render their own auth UI.
const NO_REDIRECT_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password', '/verify-email']);

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 403 && err.response?.data?.error === 'Account suspended') {
      const reason = err.response.data.reason || '';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const url = `/login?suspended=1${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`;
      if (window.location.pathname !== '/login') window.location.href = url;
      // Don't fall through to the 401 handler — suspended is its own
      // terminal state, no point queueing a second redirect.
      return Promise.reject(err);
    }
    // Approval gate (v3_35). Pending and rejected come from both
    // /api/auth/login (no JWT issued yet) and the verifyToken
    // middleware (an existing JWT was just invalidated by an admin
    // status change). Either way: drop any stored creds and route to
    // the dedicated terminal screen.
    const approvalCode = err.response?.status === 403 ? err.response?.data?.code : null;
    if (approvalCode === 'pending_approval' || approvalCode === 'rejected') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const target = approvalCode === 'pending_approval' ? '/account-pending' : '/account-rejected';
      if (window.location.pathname !== target) window.location.href = target;
      return Promise.reject(err);
    }
    if (err.response?.status === 403 && err.response?.data?.code === 'email_unverified') {
      window.dispatchEvent(new CustomEvent('owndecks:email-unverified'));
    }
    // 401 = JWT expired/invalid/missing. If the user has a stored token,
    // it's no longer valid — clear it and bounce to login. Without this,
    // expired sessions would silently fail every background request and
    // components would each have to decide what to do.
    if (err.response?.status === 401) {
      const hadToken = !!localStorage.getItem('token');
      if (hadToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (!NO_REDIRECT_PATHS.has(window.location.pathname)) {
          // Preserve where they were so we can route back after re-auth.
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?next=${next}`;
        }
      }
    }
    return Promise.reject(err);
  }
);

export const authService = {
  register: (data) =>
    api.post('/auth/register', data),
  login: (email, password, captchaToken = null) =>
    api.post('/auth/login', { email, password, captchaToken }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/password', { currentPassword, newPassword }),
  forgotPassword: (email, captchaToken = null) =>
    api.post('/auth/forgot-password', { email, captchaToken }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, newPassword }),
  verifyEmail: (token) =>
    api.post('/auth/verify-email', { token }),
  resendVerification: () =>
    api.post('/auth/resend-verification'),
};

export const artistService = {
  getAll: (params) => api.get('/artists', { params }),
  getById: (id) => api.get(`/artists/${id}`),
  create: (data) => api.post('/artists', data),
  update: (id, data) => api.put(`/artists/${id}`, data),
  addCredit: (artistId, data) => api.post(`/artists/${artistId}/credits`, data),
  getMedia: (artistId) => api.get(`/artists/${artistId}/media`),
  getVenues: (artistId) => api.get(`/artists/${artistId}/venues`),
  // Bundled aggregator for ArtistDetail mount: artist + media +
  // reviews + reviewStats + venues + verifiedGigs in one call.
  getFull: (id) => api.get(`/artists/${id}/full`),
  getByUserId: (userId) => api.get(`/artists/user/${userId}`),
  // Tech / hospitality rider — read public, write owner-only
  getRider: (artistId) => api.get(`/artists/${artistId}/rider`),
  saveRider: (artistId, riderData) => api.put(`/artists/${artistId}/rider`, { rider_data: riderData }),
  renderRiderPdf: (artistId, force = false) => api.post(`/artists/${artistId}/rider/render${force ? '?force=1' : ''}`),
  renderPressKitPdf: (artistId, includeRider = true) =>
    api.post(`/artists/${artistId}/press-kit/render${includeRider ? '?include_rider=1' : ''}`),
  // F4: press-kit theme (Pro). resetPressKitTheme returns the renderer
  // to defaults; saving any field invalidates the cached PDF so the
  // next download re-renders with the new colours.
  setPressKitTheme:    (id, theme) => api.put(`/artists/${id}/press-kit-theme`, theme),
  resetPressKitTheme:  (id) => api.delete(`/artists/${id}/press-kit-theme`),
  getMyAnalytics: () => api.get('/artists/me/analytics'),
};

export const aiService = {
  rewriteBio: (tone) => api.post('/ai/bio-rewrite', { tone }),
  draftReply: (incomingMessage, tone) => api.post('/ai/inquiry-reply', { incomingMessage, tone }),
  getQuota:   () => api.get('/ai/quota'),
};

export const bookingService = {
  create: (data) => api.post('/bookings', data),
  getForArtist: (artistId) => api.get(`/bookings/artist/${artistId}`),
  getForOrganizer: (organizerId) => api.get(`/bookings/organizer/${organizerId}`),
  getById: (id) => api.get(`/bookings/${id}`),
  getGigDay: (id) => api.get(`/bookings/${id}/gig-day`),
  updateStatus: (id, status) => api.put(`/bookings/${id}`, { status }),
  // Edit the wall-clock set time on an already-created booking.
  // Either side of the booking can call (server-side authz). Pass
  // null to clear; pass 'HH:MM' or 'HH:MM:SS' to set.
  updateSetTime: (id, setTime) => api.patch(`/bookings/${id}/set-time`, { set_time: setTime }),
  getRepeat: () => api.get('/bookings/repeat'),
  quickRebook: (data) => api.post('/bookings/quick-rebook', data),
  markCompleted: (id) => api.put(`/bookings/${id}/mark-completed`),
  share: (id) => api.post(`/bookings/${id}/share`),
  getPublic: (id) => api.get(`/bookings/public/${id}`),
};

export const messageService = {
  send: (bookingId, content, attachment = null) =>
    api.post('/messages', {
      bookingId,
      content,
      attachmentUrl: attachment?.url || null,
      attachmentMime: attachment?.mime || null,
      attachmentName: attachment?.filename || null,
      attachmentSize: attachment?.size || null,
    }),
  getForBooking: (bookingId) => api.get(`/messages/booking/${bookingId}`),
  sendInquiry: (recipientUserId, content) => api.post('/messages/inquiry', { recipientUserId, content }),
  sendInquiryBulk: (recipientUserIds, content) => api.post('/messages/inquiry-bulk', { recipientUserIds, content }),
  listInquiries: () => api.get('/messages/inquiries'),
  getInquiryThread: (otherUserId) => api.get(`/messages/inquiry/${otherUserId}`),
  getInquiryPeer: (otherUserId) => api.get(`/messages/peer/${otherUserId}`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  markRead: () => api.post('/messages/mark-read'),
  bookingReadState: (bookingId) => api.get(`/messages/booking/${bookingId}/read-state`),
  typing: (payload) => api.post('/messages/typing', payload),
};

export const organizerService = {
  create: (data) => api.post('/organizers', data),
  getById: (id) => api.get(`/organizers/${id}`),
  getByUserId: (userId) => api.get(`/organizers/user/${userId}`),
  update: (id, data) => api.put(`/organizers/${id}`, data),
  // Pro-grade organiser analytics — KPIs, funnel, 90-day spend,
  // per-artist response, per-genre conversion, repeat collabs,
  // lead-time histogram, CRM funnel.
  getMyAnalytics: () => api.get('/organizers/me/analytics'),
};

export const reviewService = {
  submit: (data) => api.post('/reviews', data),
  getForArtist: (artistId) => api.get(`/reviews/artist/${artistId}`),
  checkReviewed: (bookingId) => api.get(`/reviews/check/${bookingId}`),
};

export const notificationService = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

export const communityService = {
  // Posts paginate via offset+limit (server caps limit at 50). Default
  // 20 matches the per-spec page size and keeps the initial feed payload
  // small enough to paint without scroll-bumping.
  getPosts: (params) => api.get('/community/posts', { params }),
  createPost: (data) => api.post('/community/posts', data),
  // Bookings the calling artist can mention in a post (v3_36).
  eligibleEvents: () => api.get('/community/eligible-events'),
  // @-picker search (v3_37). Prefix-anchored, max 8.
  mentionable: (q) => api.get('/community/mentionable', { params: { q } }),
  likePost: (id) => api.post(`/community/posts/${id}/like`),
  deletePost: (id) => api.delete(`/community/posts/${id}`),
  // Comments are lazy: only fetched when a post-card expands. Keeps
  // the feed list query free of N+1 joins on the comments table.
  getComments: (postId) => api.get('/community/comments', { params: { post_id: postId } }),
  createComment: (postId, content, mentions = []) =>
    api.post('/community/comments', {
      post_id: postId,
      content,
      ...(mentions.length > 0 ? { mentions } : {}),
    }),
  deleteComment: (id) => api.delete(`/community/comments/${id}`),
};

export const paymentService = {
  getForOrganizer: (organizerId) => api.get(`/payments/organizer/${organizerId}`),
  create: (data) => api.post('/payments', data),
  updateStatus: (id, status, paidDate) => api.put(`/payments/${id}`, { status, paidDate }),
  getMineArtist: () => api.get('/payments/artist/me'),
  // CSV export — returns the absolute URL so the page can hand it to
  // a download link instead of fetching+blob-handling. Authorization
  // header is required, so we pass the token via the api axios
  // instance instead — see the caller in ArtistEarnings.
};

export const teamService = {
  getMembers: () => api.get('/team'),
  invite: (data) => api.post('/team', data),
  updateRole: (id, role) => api.put(`/team/${id}`, { role }),
  remove: (id) => api.delete(`/team/${id}`),
};

export const statsService = {
  get: () => api.get('/stats'),
};

export const followService = {
  follow: (userId) => api.post(`/users/${userId}/follow`),
  unfollow: (userId) => api.delete(`/users/${userId}/follow`),
  getStatus: (userId) => api.get(`/users/${userId}/follow-status`),
  getFollowers: (userId) => api.get(`/users/${userId}/followers`),
  getFollowing: (userId) => api.get(`/users/${userId}/following`),
  getMutualConnections: (userId) => api.get(`/users/${userId}/mutual-connections`),
};

export const ratingService = {
  submit: (data) => api.post('/ratings', data),
  getForUser: (userId) => api.get(`/ratings/users/${userId}`),
  getForCrew: (crewId) => api.get(`/ratings/crews/${crewId}`),
  getBookingStatus: (bookingId) => api.get(`/ratings/booking/${bookingId}/status`),
  getOptions: () => api.get('/ratings/options'),
  reportIncident: (data) => api.post('/ratings/incidents', data),
  withdrawIncident: (data) => api.delete('/ratings/incidents', { data }),
};

export const searchService = {
  searchArtists: (params) => api.get('/search/artists', { params }),
};

export const crmService = {
  getSavedArtists: () => api.get('/crm/saved-artists'),
  getSavedStatus: (artistId) => api.get(`/crm/saved-artists/${artistId}/status`),
  saveArtist: (artistId, data) => api.post(`/crm/saved-artists/${artistId}`, data),
  unsaveArtist: (artistId) => api.delete(`/crm/saved-artists/${artistId}`),
  updateSavedArtist: (artistId, data) => api.put(`/crm/saved-artists/${artistId}`, data),
  getEvents: () => api.get('/crm/events'),
  createEvent: (data) => api.post('/crm/events', data),
  updateEvent: (id, data) => api.put(`/crm/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/crm/events/${id}`),
  bulkBook: (eventId, artistIds) => api.post(`/crm/events/${eventId}/bulk-book`, { artist_ids: artistIds }),
  getPipeline: () => api.get('/crm/pipeline'),
};

export const feedService = {
  getFeed: (params) => api.get('/feed', { params }),
  getScene: (params) => api.get('/feed/scene', { params }),
  getSuggestions: () => api.get('/feed/suggestions'),
};

export const availabilityService = {
  getMine: () => api.get('/availability/me'),
  block: (date) => api.post('/availability/me', { date }),
  unblock: (date) => api.delete(`/availability/me/${date}`),
  check: (artistId, date) => api.get(`/availability/check/${artistId}/${date}`),
  getIcalUrl: () => api.get('/availability/ical-url'),
  regenerateIcal: () => api.post('/availability/ical-regenerate'),
};

export const matchService = {
  find: (data) => api.post('/match', data),
};

export const mediaService = {
  list: (artistId) => api.get(`/artists/${artistId}/media`),
  add: (artistId, data) => api.post(`/artists/${artistId}/media`, data),
  update: (artistId, mediaId, data) => api.patch(`/artists/${artistId}/media/${mediaId}`, data),
  remove: (artistId, mediaId) => api.delete(`/artists/${artistId}/media/${mediaId}`),
};

export const fillInService = {
  list: (params) => api.get('/fill-in', { params }),
  create: (data) => api.post('/fill-in', data),
  cancel: (id) => api.delete(`/fill-in/${id}`),
  expressInterest: (id) => api.post(`/fill-in/${id}/interest`),
  withdrawInterest: (id) => api.delete(`/fill-in/${id}/interest`),
  listInterests: (id) => api.get(`/fill-in/${id}/interests`),
};

// v3_49 source attribution. The source allowlist mirrors the backend
// allowlist in routes/profileViews.js — keep the two in sync. Pass
// undefined / unknown source and the row stores NULL.
const VIEW_SOURCES = new Set([
  'search', 'match', 'community', 'feed', 'dublin', 'crew',
  'home_rail', 'epk', 'direct', 'external',
]);

export const profileViewsService = {
  log: (userId, source) => {
    const safeSource = VIEW_SOURCES.has(source) ? source : undefined;
    return api.post(`/profile-views/${userId}`, safeSource ? { source: safeSource } : {});
  },
  getMine: () => api.get('/profile-views/me'),
};

export const proofService = {
  forBooking: (bookingId) => api.get(`/bookings/${bookingId}/proof`),
  submit: (bookingId, data) => api.post(`/bookings/${bookingId}/proof`, data),
  cosign: (bookingId) => api.post(`/bookings/${bookingId}/proof/cosign`),
  forArtist: (artistId) => api.get(`/artists/${artistId}/verified-gigs`),
};

export const organizerRatingService = {
  submit: (data) => api.post('/organizer-ratings', data),
  getForBooking: (bookingId) => api.get(`/organizer-ratings/booking/${bookingId}`),
  getForOrganizer: (organizerId) => api.get(`/organizer-ratings/organizer/${organizerId}`),
};

export const sceneService = {
  dublin: () => api.get('/scene/dublin'),
};

export const discoveryService = {
  rails: () => api.get('/discovery'),
  homeRails: () => api.get('/discovery/home-rails'),
};

export const venueService = {
  search: (q) => api.get('/venues', { params: { q } }),
  list: (params) => api.get('/venues', { params }),
  getBySlug: (slug) => api.get(`/venues/${slug}`),
  create: (data) => api.post('/venues', data),
  update: (id, data) => api.put(`/venues/${id}`, data),
  remove: (id) => api.delete(`/venues/${id}`),
};

export const crewService = {
  list: (params) => api.get('/crews', { params }),
  mine: () => api.get('/crews/mine'),
  getBySlug: (slug) => api.get(`/crews/${slug}`),
  create: (data) => api.post('/crews', data),
  update: (id, data) => api.put(`/crews/${id}`, data),
  remove: (id) => api.delete(`/crews/${id}`),
  addMember: (id, email, role) => api.post(`/crews/${id}/members`, { email, role }),
  removeMember: (id, userId) => api.delete(`/crews/${id}/members/${userId}`),
  setRole: (id, userId, role) => api.put(`/crews/${id}/members/${userId}`, { role }),
  bookings: (id) => api.get(`/bookings/crew/${id}`),
};

export const proService = {
  // Records a "Notify me when Pro launches" click. Idempotent.
  expressInterest: () => api.post('/users/me/pro-interest'),
};

export const savedSearchService = {
  list:    () => api.get('/saved-searches'),
  create:  ({ name, filters }) => api.post('/saved-searches', { name, filters }),
  patch:   (id, body) => api.patch(`/saved-searches/${id}`, body),
  remove:  (id) => api.delete(`/saved-searches/${id}`),
};

export const verificationService = {
  // F3 — verified badge fast-track. Self-service request + status read.
  me:      () => api.get('/verification/me'),
  request: () => api.post('/verification/request'),
  cancel:  () => api.post('/verification/cancel'),
};

export const invoiceService = {
  // Returns the PDF as a Blob. Caller turns it into an object URL +
  // triggers a download — see ArtistBookings.downloadInvoice().
  downloadForBooking: (bookingId) =>
    api.get(`/invoices/booking/${bookingId}`, { responseType: 'blob' }),
};

export const uploadService = {
  // Posts multipart/form-data with field "file". Returns { url }.
  // kind = 'avatar' (default) or 'banner' — picks the storage folder.
  avatar: (file, kind = 'avatar') => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/uploads/avatar?kind=${kind}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // PDF upload for booking attachments. Returns { url, filename, size }.
  document: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/uploads/document', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Message attachment — image, video, or PDF up to 25MB.
  // Returns { url, mime, filename, size } for the message-send call.
  messageAttachment: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/uploads/message-attachment', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const adminService = {
  listUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  suspend: (id, reason) => api.put(`/admin/users/${id}/suspend`, { reason }),
  unsuspend: (id) => api.put(`/admin/users/${id}/unsuspend`),
  // v3_48: enforcement levels 1 + 2.
  warn:        (id, payload) => api.post(`/admin/users/${id}/warn`, payload),
  warnings:    (id)          => api.get(`/admin/users/${id}/warnings`),
  cooldown:    (id, payload) => api.post(`/admin/users/${id}/cooldown`, payload),
  setAdmin: (id, isAdmin) => api.put(`/admin/users/${id}/admin`, { isAdmin }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`, { data: { confirm: 'DELETE' } }),
  stats: () => api.get('/admin/stats'),
  // Phase B: filterable audit log. `limit` stays positional for
  // backwards-compat with the older callsites; pass an object to
  // include action / target_type / admin_email / since.
  auditLog: (paramsOrLimit = {}) => {
    const params = typeof paramsOrLimit === 'number' ? { limit: paramsOrLimit } : paramsOrLimit;
    return api.get('/admin/audit-log', { params });
  },
  listContent: (type) => api.get(`/admin/content/${type}`),
  deleteContent: (type, id) => api.delete(`/admin/content/${type}/${id}`),
  proInterest: () => api.get('/admin/pro-interest'),

  // Bookings
  listBookings: (params) => api.get('/admin/bookings', { params }),
  forceBookingStatus: (id, status) => api.put(`/admin/bookings/${id}/force-status`, { status }),
  cancelBooking: (id) => api.delete(`/admin/bookings/${id}`),
  bookingThread: (id) => api.get(`/admin/bookings/${id}/thread`),

  // Approval queue (v3_35)
  approveUser: (id) => api.post(`/admin/users/${id}/approve`),
  rejectUser: (id, reason) => api.post(`/admin/users/${id}/reject`, { reason }),

  // User detail / actions
  userTimeline: (id) => api.get(`/admin/users/${id}/timeline`),
  resetPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  forceReverify: (id) => api.post(`/admin/users/${id}/force-reverify`),
  forceLogout: (id) => api.post(`/admin/users/${id}/force-logout`),
  impersonate: (id) => api.post(`/admin/users/${id}/impersonate`),
  toggleVerified: (id, verified) => api.post(`/admin/users/${id}/verify`, { verified }),
  setTier:        (id, tier)      => api.post(`/admin/users/${id}/tier`, { tier }),

  // Verification request queue (F3 — verified badge fast-track)
  verificationQueue:   () => api.get('/admin/verification-requests'),
  verificationApprove: (id) => api.post(`/admin/verification-requests/${id}/approve`),
  verificationReject:  (id, reason) => api.post(`/admin/verification-requests/${id}/reject`, { reason }),

  // Reports queue
  listReports: (params) => api.get('/admin/reports', { params }),
  resolveReport: (id, status, note) => api.put(`/admin/reports/${id}`, { status, note }),

  // Email events
  emailEvents: (recipient) => api.get('/admin/email-events', { params: { recipient } }),

  // Reviews moderation
  listReviews: (params) => api.get('/admin/reviews', { params }),
  hideReview: (id, reason) => api.put(`/admin/reviews/${id}/hide`, { reason }),
  unhideReview: (id) => api.put(`/admin/reviews/${id}/unhide`),

  // Crews
  listCrews: () => api.get('/admin/crews'),
  banCrew: (id, banned) => api.put(`/admin/crews/${id}/ban`, { banned }),

  // DM viewer (audit-logged)
  viewDmThread: (userA, userB, reportId) =>
    api.get(`/admin/inquiries/${userA}/${userB}`, { params: { reportId } }),

  // Broadcasts
  sendBroadcast: (data) => api.post('/admin/broadcasts', data),
  broadcastHistory: () => api.get('/admin/broadcasts'),
};

export const reportService = {
  // User-facing: any authed user can flag content
  create: (data) => api.post('/reports', data),
};

export default api;
